import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PublicNavbar } from '@/components/Layout/PublicNavbar';
import { PasswordField } from '@/components/forms/PasswordField';
import {
  resetPasswordSchema,
  type ResetPasswordFormData,
} from '@/lib/validations/auth';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

// Parses a URL hash like `#access_token=…&type=recovery&refresh_token=…` into
// a plain object. Returns null when there's no recoverable hash so the page
// can render a "link inválido" state. Exported for unit testing.
export type RecoveryHash = {
  accessToken: string | null;
  refreshToken: string | null;
  type: string | null;
  errorDescription: string | null;
};

export const parseRecoveryHash = (hash: string): RecoveryHash => {
  // Strip both '#' and '?' so we still parse correctly if Supabase ever
  // moves the recovery payload onto the query string.
  const params = new URLSearchParams(hash.replace(/^[#?]/, ''));
  return {
    accessToken: params.get('access_token'),
    refreshToken: params.get('refresh_token'),
    type: params.get('type'),
    errorDescription: params.get('error_description'),
  };
};

// Returns true when the parsed hash has every field needed to call
// `supabase.auth.setSession`. Accepts both 'recovery' (password reset) and
// 'invite' (event-access invitation) flows — Supabase emits the latter from
// `auth.admin.inviteUserByEmail`. Exported for testing.
export const isValidRecoveryHash = (parsed: RecoveryHash): boolean =>
  (parsed.type === 'recovery' || parsed.type === 'invite') &&
  !!parsed.accessToken &&
  !!parsed.refreshToken &&
  !parsed.errorDescription;

export const REDIRECT_DELAY_MS = 2000;

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [status, setStatus] = React.useState<
    'checking' | 'ready' | 'invalid' | 'success'
  >('checking');
  const [hashError, setHashError] = React.useState<string | null>(null);
  // `?invited=1` is set by the inviteEventUser Edge Function when redirecting
  // a newly-invited guest user. Switches the copy so it doesn't say "recover
  // your password" when really they're choosing one for the first time.
  const isInvitedUser = React.useMemo(
    () => new URLSearchParams(window.location.search).get('invited') === '1',
    [],
  );

  const {
    register,
    handleSubmit,
    watch,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  const watchedPassword = watch('password');

  // On mount, validate the recovery token from the URL hash. Supabase places
  // it there as `#access_token=…&type=recovery&refresh_token=…`. We exchange
  // it for an active session via `setSession` so the subsequent `updateUser`
  // call authorises against that session.
  React.useEffect(() => {
    const hash = window.location.hash;
    const parsed = parseRecoveryHash(hash);

    if (parsed.errorDescription) {
      setHashError(parsed.errorDescription);
      setStatus('invalid');
      return;
    }

    if (!isValidRecoveryHash(parsed)) {
      setStatus('invalid');
      return;
    }

    let cancelled = false;
    (async () => {
      const { error } = await supabase.auth.setSession({
        access_token: parsed.accessToken!,
        refresh_token: parsed.refreshToken!,
      });
      if (cancelled) return;
      if (error) {
        setHashError(error.message);
        setStatus('invalid');
        return;
      }
      setStatus('ready');
      // Clean the hash so the token doesn't leak into screenshots or
      // back-button navigation.
      window.history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      const { error } = await supabase.auth.updateUser({
        password: data.password,
      });

      if (error) {
        setError('root', { message: error.message });
        toast({
          variant: 'destructive',
          title: 'No pudimos actualizar tu contraseña',
          description: error.message,
        });
        return;
      }

      setStatus('success');
      toast({
        title: 'Contraseña actualizada',
        description: 'Te llevaremos al inicio de sesión en unos segundos.',
      });

      // Sign the recovery session out so the user logs in with the new
      // credentials cleanly, then redirect.
      await supabase.auth.signOut();

      setTimeout(() => {
        navigate('/login', { replace: true });
      }, REDIRECT_DELAY_MS);
    } catch (err) {
      setError('root', {
        message: 'Error inesperado. Por favor intenta de nuevo.',
      });
      toast({
        variant: 'destructive',
        title: 'No pudimos actualizar tu contraseña',
        description: 'Error inesperado. Por favor intenta de nuevo.',
      });
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PublicNavbar />
        <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow-brand sm:rounded-lg sm:px-10 text-center border border-neutral-200">
              <p className="text-body-md text-neutral-600">
                Validando enlace de recuperación...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PublicNavbar />
        <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow-brand sm:rounded-lg sm:px-10 text-center border border-neutral-200">
              <h2 className="text-h2 font-semibold mb-4 text-brand-primary">
                Enlace inválido o expirado
              </h2>
              <p className="text-body-md text-neutral-600 mb-6">
                Este enlace de recuperación ya no es válido. Solicita uno nuevo
                desde la página de recuperar contraseña.
              </p>
              {hashError && (
                <p className="text-body-sm text-neutral-500 mb-6">
                  Detalle: {hashError}
                </p>
              )}
              <div className="space-y-3">
                <Link to="/forgot-password">
                  <Button className="w-full btn-primary shadow-sm">
                    Solicitar un nuevo enlace
                  </Button>
                </Link>
                <Link to="/login">
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Volver a iniciar sesión
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-neutral-50">
        <PublicNavbar />
        <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow-brand sm:rounded-lg sm:px-10 text-center border border-neutral-200">
              <div className="w-16 h-16 bg-semantic-success-light rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-semantic-success" />
              </div>
              <h2 className="text-h2 font-semibold mb-4 text-brand-primary">
                ¡Contraseña actualizada!
              </h2>
              <p className="text-body-md text-neutral-600 mb-6">
                Tu contraseña se cambió correctamente. Te llevaremos al inicio
                de sesión en unos segundos.
              </p>
              <Link to="/login">
                <Button className="w-full btn-primary shadow-sm">
                  Ir a iniciar sesión
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // status === 'ready'
  return (
    <div className="min-h-screen bg-neutral-50">
      <PublicNavbar />
      <div className="pt-32 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center mb-8">
            <h2 className="text-h1 font-bold text-brand-primary">
              {isInvitedUser ? 'Bienvenido a CoPlanner' : 'Define tu nueva contraseña'}
            </h2>
            <p className="mt-2 text-body-md text-neutral-600">
              {isInvitedUser
                ? 'Define una contraseña para acceder al evento al que te invitaron.'
                : 'Elige una contraseña segura para volver a entrar a tu cuenta.'}
            </p>
          </div>

          <div className="bg-white py-8 px-4 shadow-brand sm:rounded-lg sm:px-10 border border-neutral-200">
            <form
              className="space-y-6"
              onSubmit={handleSubmit(onSubmit)}
              noValidate
            >
              {errors.root && (
                <div
                  role="alert"
                  className="bg-semantic-error-light border border-semantic-error text-semantic-error px-4 py-3 rounded-md"
                >
                  {errors.root.message}
                </div>
              )}

              <PasswordField
                label="Nueva contraseña"
                name="password"
                autoComplete="new-password"
                required
                placeholder="Mínimo 8 caracteres"
                error={errors.password}
                value={watchedPassword}
                showStrength
                {...register('password')}
              />

              <PasswordField
                label="Confirmar contraseña"
                name="confirmPassword"
                autoComplete="new-password"
                required
                placeholder="Repite tu nueva contraseña"
                error={errors.confirmPassword}
                {...register('confirmPassword')}
              />

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary shadow-sm"
              >
                {isSubmitting ? 'Actualizando...' : 'Actualizar contraseña'}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="flex items-center justify-center text-sm text-brand-primary hover:text-brand-primary-hover"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Volver a iniciar sesión
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
